import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box } from '@material-ui/core';

import { EmailCampaignPerformanceGraph, StatBox } from 'views/Reports/components';
import { types as emailStatBoxTypes } from 'views/Reports/constants';

const useStyles = makeStyles(theme => ({
  performanceGraphContainer: {
    marginTop: theme.spacing(4)
  }
}));

const providerMetricMap = {
  ACCEPTED: 'ACCEPTED',
  DELIVERED: 'DELIVERED',
  OPENED: 'OPENED',
  CLICKED: 'CLICKED',
  BOUNCED: 'BOUNCED',
  BLOCKED: 'BLOCKED'
};

const EmailCampaignDetail = props => {
  const { report, onStatClick } = props;
  const classes = useStyles();
  const { reportData, getEmailStats, isLoading } = report;
  const [emailStats, setEmailStats] = useState([]);

  const requestedCount = reportData?.totalMetrics?.totalRequested?.totalCount || 0;
  const acceptedCount = reportData?.totalMetrics?.undeliveredMetrics?.totalAccepted?.totalCount || 0;
  const deliveredCount = reportData?.totalMetrics?.totalDelivered?.totalCount || 0;
  const openedCount = reportData?.totalMetrics?.totalOpened?.totalCount || 0;
  const clickedCount = reportData?.totalMetrics?.totalClicked?.totalCount || 0;

  const acceptedRate = requestedCount > 0 ? Math.round((acceptedCount / requestedCount) * 100) : 0;
  const deliveredRate = acceptedCount > 0 ? Math.round((deliveredCount / acceptedCount) * 100) : 0;
  const openRate = deliveredCount > 0 ? Math.round((openedCount / deliveredCount) * 100) : 0;
  const clickRate = openedCount > 0 ? Math.round((clickedCount / openedCount) * 100) : 0;

  const displayStats = useMemo(() => {
    return emailStats.map(box => {
      if (box.statType === emailStatBoxTypes.REQUESTED) {
        return box;
      }

      if (box.statType === emailStatBoxTypes.ACCEPTED) {
        return {
          ...box,
          subStatText: `${acceptedRate}% Acceptance Rate`,
          subStatTooltip: `${acceptedCount} Accepted out of ${requestedCount} Requested`,
          subStatValue: acceptedRate
        };
      }

      if (box.statType === emailStatBoxTypes.DELIVERED) {
        return {
          ...box,
          subStatText: `${deliveredRate}% Delivery Rate`,
          subStatTooltip: `${deliveredCount} Delivered out of ${acceptedCount} Accepted`,
          subStatValue: deliveredRate
        };
      }

      if (box.statType === emailStatBoxTypes.OPENED) {
        return {
          ...box,
          subStatText: `${openRate}% Open Rate`,
          subStatTooltip: `${openedCount} Opened out of ${deliveredCount} Delivered`,
          subStatValue: openRate
        };
      }

      if (box.statType === emailStatBoxTypes.CLICKED) {
        return {
          ...box,
          subStatText: `${clickRate}% Click Rate`,
          subStatTooltip: `${clickedCount} Clicked out of ${openedCount} Opened`,
          subStatValue: clickRate
        };
      }

      return box;
    });
  }, [emailStats, acceptedRate, deliveredRate, openRate, clickRate, acceptedCount, requestedCount, openedCount, deliveredCount, clickedCount]);

  useEffect(() => {
    setEmailStats(getEmailStats(reportData));
  }, [report]);

  return (
    <div>
      <Grid container spacing={4}>
        {displayStats.map((box, i) => {
          const metricKey = providerMetricMap[box.statType];
          return (
            <Grid key={i} item lg={3} sm={6} xl={2} xs={12}>
              <StatBox
                currentStat={box.currentStat}
                previousStat={1029}
                period="day"
                statType={box.statType}
                isReportLoading={isLoading}
                subStatText={box.subStatText}
                subStatTooltip={box.subStatTooltip}
                subStatValue={box.subStatValue}
                onClick={metricKey && onStatClick ? () => onStatClick(metricKey) : undefined}
              />
            </Grid>
          );
        })}
      </Grid>

      <Box className={classes.performanceGraphContainer}>
        <EmailCampaignPerformanceGraph report={report} />
      </Box>
    </div>
  );
};

export default EmailCampaignDetail;
